/**
 * Auth Modal Component - Prompts user to authenticate with GitHub
 */

import { useState, useEffect } from 'preact/hooks';
import { Dialog } from './Dialog';
import { signInWithGithub, onAuthChange } from '../globals/firebase';
import { ShieldUserIcon } from 'lucide-preact';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: () => void;
}

export function AuthModal({ isOpen, onClose, onAuthSuccess }: AuthModalProps) {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Watch for authentication state changes
  useEffect(() => {
    if (!isOpen) return;

    const unsubscribe = onAuthChange((user) => {
      if (user) {
        // User successfully authenticated
        setIsSigningIn(false);
        onAuthSuccess();
      }
    });

    return () => unsubscribe();
  }, [isOpen, onAuthSuccess]);

  const handleSignIn = async () => {
    setIsSigningIn(true);
    setError(null);

    try {
      await signInWithGithub();
      // The auth state change listener will handle success
    } catch (err: any) {
      console.error('Sign in failed:', err);
      setError(err.message || 'Failed to sign in with GitHub');
      setIsSigningIn(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    setIsSigningIn(false);
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleCancel} title="Authentication Required">
      <div class="p-6 col gap-8 aic jcc h-full">
        <ShieldUserIcon class="w-48 h-48 text-neutral-800" strokeWidth={1.8} />
        <p class="text-(neutral-400 center) leading-relaxed max-w-[30ch]">
          You need an account to install or manage your apps and purchases.
        </p>
        {error && (
          <div class="p-4 bg-red-900/20 border border-red-800 rounded-lg">
            <p class="text-red-400 text-sm">{error}</p>
          </div>
        )}
        <button
          onClick={handleSignIn}
          class="row aic gap-4 px-6 py-3 text-lg font-medium bg-neutral-800 hover:bg-neutral-700 rounded-full transition-all duration-150 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSigningIn}
        >
          {isSigningIn ? (
            <>
              <span class="spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Signing in...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-neutral-500"><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" /><path d="M9 18c-4.51 2-5-2-7-2" /></svg>
              <span>Sign in with GitHub</span>
            </>
          )}
        </button>
      </div>
    </Dialog>
  );
}
